<template>
  <div>
    <!-- Templates List Card -->
    <b-card class="templates-list-card">
      <!-- Gradient Line Wrapper -->
      <div class="gradient-line-wrapper"></div>
      <!-- Table Header -->
      <div class="table-header d-flex flex-wrap justify-content-between align-items-center mb-2">
        <div>
          <h5 class="mb-0 template-title">Templates</h5>
          <small class="text-muted">Manage and review templates</small>
        </div>
      </div>



      <!-- Filter Row -->
      <b-row class="align-items-center filter-row-single-line mb-2">
        <!-- Search Input -->
        <b-col xl="3" lg="3" md="4" sm="12" cols="12">
          <b-form-input
            v-model="searchQuery"
            placeholder="Search templates..."
            class="filter-control"
            @input="onSearchChange"
            debounce="500"
          />
        </b-col>
        <!-- Template For Filter -->
        <b-col xl="3" lg="3" md="4" sm="12" cols="12">
          <v-select
            v-model="templateFor"
            :options="templateForOptions"
            placeholder="Template For (All)"
            @input="onTemplateForChange"
            class="filter-select"
            :clearable="true"
          />
        </b-col>
        <!-- Action Buttons -->
        <b-col xl="6" lg="6" md="7" sm="12" cols="12" class="text-right">
          <div class="d-flex gap-2 align-items-center justify-content-end flex-wrap">
            <b-button
              v-if="rights.add"
              @click="openAddModal"
              variant="gradient-primary"
              class="action-btn-filter"
              :disabled="saving"
            >
              <b-spinner v-if="saving" small class="mr-1" />
              <feather-icon v-else icon="PlusIcon" size="16" class="mr-1" />
              Add Template
            </b-button>
          </div>
        </b-col>
      </b-row>

      <!-- Pagination Controls - Top (Compact Single Line) -->
      <div class="pagination-controls-top d-flex justify-content-between align-items-center mb-2">
        <div class="d-flex align-items-center gap-2 flex-wrap">
          <!-- Page Size Dropdown -->
          <div class="page-size-wrapper">
            <label class="page-size-label">Show:</label>
            <v-select
              :key="'page-size-' + perPage"
              v-model="perPage"
              :options="pageSizeOptions"
              :reduce="option => option.value"
              label="label"
              :clearable="false"
              :searchable="false"
              append-to-body
              class="page-size-select-compact"
              @input="onPageSizeChange"
              @option:selected="closePageSizeDropdown"
              ref="pageSizeSelect"
            />
          </div>
        </div>
        
        <!-- Count Display in Middle -->
        <div class="pagination-count-display">
          <span class="count-text">
            Showing <strong>{{ displayedRange.start }}</strong> to <strong>{{ displayedRange.end }}</strong> of <strong>{{ totalCount }}</strong>
          </span>
        </div>
        
        <!-- Pagination -->
        <div class="pagination-wrapper-compact">
          <b-pagination
            v-model="currentPage"
            :total-rows="totalCount"
            :per-page="perPage"
            size="sm"
            align="center"
            class="pagination-compact"
            @change="onPageChange"
          />
        </div>
      </div>

      <!-- Data Grid -->
      <b-table
        class="templates-table"
        :items="templates"
        :fields="fields"
        :busy="tableBusy"
        :responsive="false"
        hover
        show-empty
        :empty-text="tableEmptyText"
      >
        <template #table-busy>
          <tbody>
            <tr v-for="n in 5" :key="'skeleton-' + n" class="skeleton-row">
              <td v-for="(field, index) in fields" :key="'skeleton-cell-' + index" class="skeleton-cell">
                <div class="skeleton-loader"></div>
              </td>
            </tr>
          </tbody>
        </template>

        <template #cell(sno)="{ index }">
          <span class="text-muted">{{ (currentPage - 1) * perPage + index + 1 }}</span>
        </template>

        <template #cell(templateFor)="{ item }">
          <b-badge :variant="item.templateFor === 'Staff' ? 'light-primary' : 'light-success'" class="template-for-badge">
            {{ item.templateFor || 'N/A' }}
          </b-badge>
        </template>

        <template #cell(title)="{ item }">
          <span class="template-cell">{{ item.title || 'N/A' }}</span>
        </template>

        <template #cell(format)="{ item }">
          <div class="template-cell-truncated">
            <span v-if="getPlainTextFromFormat(item.format).length > 100">
              {{ getPlainTextFromFormat(item.format).substring(0, 100) }}...
            </span>
            <span v-else>{{ getPlainTextFromFormat(item.format) || 'N/A' }}</span>
          </div>
        </template>

        <template #cell(actions)="{ item }">
          <div class="d-flex align-items-center justify-content-end">
            <b-button
              variant="primary"
              v-b-tooltip.hover.left
              title="View"
              size="sm"
              class="action-btn-view mr-1"
              @click="viewTemplate(item)"
            >
              <feather-icon icon="EyeIcon" size="16" />
            </b-button>
            <b-button
              v-if="rights.edit"
              variant="outline-success"
              v-b-tooltip.hover.left
              title="Edit"
              size="sm"
              class="action-btn-table mr-1"
              @click="editTemplate(item)"
              :disabled="editLoading === item.id || deleteLoading === item.id"
            >
              <b-spinner v-if="editLoading === item.id" small type="grow" />
              <feather-icon v-else icon="EditIcon" size="16" />
            </b-button>
            <b-button
              v-if="rights.delete"
              variant="outline-danger"
              v-b-tooltip.hover.left
              title="Delete"
              size="sm"
              class="action-btn-outline"
              @click="deleteTemplate(item)"
              :disabled="editLoading === item.id || deleteLoading === item.id"
            >
              <b-spinner v-if="deleteLoading === item.id" small type="grow" />
              <feather-icon v-else icon="Trash2Icon" size="16" />
            </b-button>
          </div>
        </template>
      </b-table>
    </b-card>

    <!-- Add/Edit Modal -->
    <b-modal
      v-model="showModal"
      :title="isEdit ? 'Edit Template' : 'Add Template'"
      @hidden="resetForm"
      centered
      size="lg"
      no-close-on-backdrop
      class="template-modal"
    >
      <b-form @submit.prevent>
        <b-form-group label="Title" label-for="title">
          <b-form-input
            id="title"
            v-model="form.title"
            placeholder="Enter template title"
            :disabled="saving"
            class="modern-input"
          />
        </b-form-group>

        <b-form-group label="Template For" label-for="templateFor">
          <v-select
            id="templateFor"
            v-model="form.templateFor"
            :options="templateForOptions"
            placeholder="Select Template Type"
            :disabled="saving"
            class="modern-select"
          />
        </b-form-group>

        <!-- Advanced Keywords Panel -->
        <div v-if="form.templateFor" class="keywords-panel-v2 mb-2">
          <div class="panel-header-v2 d-flex align-items-center justify-content-between mb-1">
            <div class="d-flex align-items-center">
              <div class="panel-icon-wrapper mr-75">
                <feather-icon icon="ZapIcon" size="14" />
              </div>
              <div>
                <h6 class="panel-title-v2 mb-0">Smart Placeholders</h6>
                <small class="text-muted">Click to insert dynamic variables</small>
              </div>
            </div>
            <feather-icon 
              icon="HelpCircleIcon" 
              size="16" 
              class="text-muted cursor-pointer hover-primary" 
              v-b-tooltip.hover 
              title="These placeholders will be automatically replaced with actual data when the message is sent."
            />
          </div>
          
          <div class="keywords-scroll-area">
            <div class="keywords-grid-v2">
              <div
                v-for="keyword in currentKeywords"
                :key="keyword.value"
                class="modern-keyword-chip-v2"
                @click="insertKeyword(keyword.value)"
                v-b-tooltip.hover.top="`Insert ${keyword.value}`"
              >
                <div class="chip-body-v2">
                  <span class="chip-label-v2">{{ keyword.label }}</span>
                  <span class="chip-tag-v2">{{ keyword.value }}</span>
                </div>
                <div class="chip-action-v2">
                  <feather-icon icon="PlusIcon" size="12" />
                </div>
              </div>
            </div>
          </div>
        </div>

        <b-form-group label="Format" label-for="format">
          <quill-editor
            id="format"
            ref="quillEditor"
            v-model="form.format"
            :options="{
              modules: {
                toolbar: editorOptions,
              },
              placeholder: 'Enter template format',
              theme: 'snow',
            }"
            :disabled="saving"
            class="quill-editor-wrapper"
          />
        </b-form-group>
      </b-form>

      <template #modal-footer>
        <b-button variant="outline-secondary" @click="showModal = false" :disabled="saving" class="btn-modern-cancel">
          Cancel
        </b-button>
        <b-button variant="primary" @click="saveTemplate" :disabled="saving || !isFormValid" class="btn-modern-primary">
          <b-spinner v-if="saving" small class="mr-2" />
          <feather-icon v-else icon="SaveIcon" size="16" class="mr-2" />
          {{ saving ? 'Saving...' : isEdit ? 'Update' : 'Save' }}
        </b-button>
      </template>
    </b-modal>

    <!-- View Modal -->
    <b-modal
      v-model="showViewModal"
      title="View Template"
      @hidden="resetViewModal"
      centered
      size="lg"
      no-close-on-backdrop
      class="template-modal"
    >
      <div class="view-template-content">
        <b-form-group label="Title">
          <div class="view-field-value">{{ viewData.title || 'N/A' }}</div>
        </b-form-group>

        <b-form-group label="Template For">
          <div class="view-field-value">
             <b-badge :variant="viewData.templateFor === 'Staff' ? 'light-primary' : 'light-success'">
              {{ viewData.templateFor || 'N/A' }}
            </b-badge>
          </div>
        </b-form-group>

        <b-form-group label="Format">
          <div class="view-format-content">{{ getPlainTextFromFormat(viewData.format) || 'N/A' }}</div>
        </b-form-group>
      </div>

      <template #modal-footer>
        <b-button variant="outline-secondary" @click="showViewModal = false" class="btn-modern-cancel">
          Close
        </b-button>
      </template>
    </b-modal>
  </div>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BButton,
  BTable,
  BPagination,
  BForm,
  BFormGroup,
  BFormInput,
  BSpinner,
  BModal,
  VBTooltip,
  BBadge,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import { quillEditor } from 'vue-quill-editor'
import { mapActions } from 'vuex'

export default {
  name: 'Template',
  components: {
    BCard,
    BRow,
    BCol,
    BButton,
    BTable,
    BPagination,
    BForm,
    BFormGroup,
    BFormInput,
    BSpinner,
    BModal,
    vSelect,
    quillEditor,
    BBadge,
  },
  directives: {
    'b-tooltip': VBTooltip,
  },
  data() {
    return {
      rights: {},
      templates: [],
      dataLoading: false,
      saving: false,
      templatesCache: {},
      cacheTTL: 2 * 60 * 1000, // 2 minutes
      currentRequestKey: null,
      editLoading: null,
      deleteLoading: null,
      currentPage: 1,
      perPage: 10,
      totalCount: 0,
      searchQuery: '',
      searchTimeout: null,
      showModal: false,
      showViewModal: false,
      isEdit: false,
      templateFor: null,
      templateForOptions: ['Staff', 'Student'],
      viewData: {
        title: '',
        format: '',
        templateFor: '',
      },
      pageSizeOptions: [
        { label: '10', value: 10 },
        { label: '20', value: 20 },
        { label: '40', value: 40 },
        { label: '80', value: 80 },
        { label: '100', value: 100 },
        { label: '200', value: 200 }
      ],
      fields: [
        { key: 'sno', label: 'S.No', sortable: false, thStyle: { width: '80px', textAlign: 'center' }, tdClass: 'text-center' },
        { key: 'title', label: 'Title', sortable: false, thStyle: { width: 'auto', minWidth: '200px' } },
        { key: 'templateFor', label: 'Template For', sortable: false, thStyle: { width: '150px' } },
        { key: 'format', label: 'Format', sortable: false, thStyle: { width: 'auto', minWidth: '300px' } },
        { key: 'actions', label: 'Actions', sortable: false, thStyle: { width: '180px', textAlign: 'center' }, tdClass: 'actions-cell text-center' },
      ],
      form: {
        id: 0,
        title: '',
        format: '',
        templateFor: 'Student', 
      },
      editorOptions: [
        ["bold", "italic", "underline", "strike"], // toggled buttons
        ["blockquote"],
        [{ header: 1 }, { header: 2 }], // custom button values
        [{ list: "ordered" }, { list: "bullet" }],
        [{ script: "sub" }, { script: "super" }], // superscript/subscript
        [{ indent: "-1" }, { indent: "+1" }], // outdent/indent
        [{ direction: "rtl" }], // text direction
        [{ size: ["small", false, "large", "huge"] }], // custom dropdown
        [{ header: [1, 2, 3, 4, 5, 6, false] }],
        [{ color: [] }, { background: [] }], // dropdown with defaults from theme
        [{ align: [] }],
        ["link"],
      ],
      studentKeywords: [
        { label: 'Student Name', value: '{{student_name}}' },
        { label: 'Father Name', value: '{{father_name}}' },
        { label: 'GR No', value: '{{gr_no}}' },
        { label: 'Class', value: '{{class}}' },
        { label: 'Section', value: '{{section}}' },
        { label: 'Family Code', value: '{{family_code}}' },
        { label: 'Campus', value: '{{campus}}' },
        { label: 'Address', value: '{{address}}' },
        { label: 'Contact', value: '{{contact}}' },
      ],
      staffKeywords: [
        { label: 'Staff Name', value: '{{staff_name}}' },
        { label: 'Father Name', value: '{{father_name}}' },
        { label: 'Designation', value: '{{designation}}' },
        { label: 'Department', value: '{{department}}' },
        { label: 'Campus', value: '{{campus}}' },
        { label: 'Contact Number', value: '{{contact_number}}' },
        { label: 'CNIC', value: '{{cnic}}' },
      ],
    }
  },
  computed: {
    isFormValid() {
      // Check if title is valid
      const titleValid = this.form.title && this.form.title.trim().length > 0
      const templateForValid = !!this.form.templateFor
      
      // Check if format is valid (HTML content from Quill editor)
      // Remove HTML tags and check if there's actual text content
      let formatValid = false
      if (this.form.format) {
        const tempDiv = document.createElement('div')
        tempDiv.innerHTML = this.form.format
        const textContent = tempDiv.textContent || tempDiv.innerText || ''
        formatValid = textContent.trim().length > 0
      }
      
      return titleValid && formatValid && templateForValid
    },
    currentKeywords() {
      if (this.form.templateFor === 'Student') {
        return this.studentKeywords
      } else if (this.form.templateFor === 'Staff') {
        return this.staffKeywords
      }
      return []
    },
    tableBusy() {
      return this.dataLoading
    },
    tableEmptyText() {
      return this.tableBusy
        ? ''
        : 'No templates found'
    },
    displayedRange() {
      if (this.totalCount === 0) {
        return { start: 0, end: 0 }
      }
      const start = (this.currentPage - 1) * this.perPage + 1
      const end = Math.min(this.currentPage * this.perPage, this.totalCount)
      return { start, end }
    },
  },
  created() {
    // Initialize rights from menu and enforce view permission
    let right = {};
    this.$store.state.menu.forEach((el) => {
      el.children.forEach((ch) => {
        right[ch.route] = {
          view: ch.view,
          add: ch.add,
          edit: ch.edit,
          delete: ch.delete,
          assignSections: ch.assignSections,
        };
      });
    });
    this.$store.commit("setRights", right);

    if (!this.$store.state.rights[this.$route.name]) {
      this.$router.replace({ name: "misc-not-authorized" });
      return;
    }
    this.rights = this.$store.state.rights[this.$route.name];
    if (!this.rights.view) {
      this.$router.replace({ name: "misc-not-authorized" });
      return;
    }

    this.loadTemplates()
  },
  
  mounted() {
    // Add click-outside handler to close v-select dropdowns
    this.handleClickOutside = (event) => {
      const clickedElement = event.target;
      const isVSelectClick = clickedElement.closest('.v-select') || 
                            clickedElement.closest('.vs__dropdown-menu') ||
                            clickedElement.closest('.vs__dropdown-option') ||
                            clickedElement.closest('.vs__dropdown-toggle') ||
                            clickedElement.closest('.vs__actions') ||
                            clickedElement.closest('.vs__selected-options');
      
      if (!isVSelectClick) {
        const vSelectRefs = ['pageSizeSelect'];
        vSelectRefs.forEach((refName) => {
          if (this.$refs[refName] && this.$refs[refName].$el) {
            const toggle = this.$refs[refName].$el.querySelector('.vs__dropdown-toggle');
            if (toggle) {
              toggle.blur();
            }
          }
        });
        
        const allToggles = document.querySelectorAll('.vs__dropdown-toggle');
        allToggles.forEach((toggle) => {
          if (document.activeElement === toggle) {
            toggle.blur();
          }
        });
        
        const dropdownMenus = document.querySelectorAll('.vs__dropdown-menu');
        dropdownMenus.forEach((menu) => {
          const style = window.getComputedStyle(menu);
          if (style.display !== 'none' && style.visibility !== 'hidden') {
            const allVSelects = document.querySelectorAll('.v-select');
            allVSelects.forEach((vSelect) => {
              const toggle = vSelect.querySelector('.vs__dropdown-toggle');
              if (toggle && document.activeElement === toggle) {
                toggle.blur();
              }
            });
          }
        });
        
        if (document.activeElement && document.activeElement.classList.contains('vs__dropdown-toggle')) {
          document.activeElement.blur();
        }
      }
    };
    
    document.addEventListener('click', this.handleClickOutside, true);
    document.addEventListener('mousedown', this.handleClickOutside, true);
  },
  
  beforeDestroy() {
    if (this.handleClickOutside) {
      document.removeEventListener('click', this.handleClickOutside, true);
      document.removeEventListener('mousedown', this.handleClickOutside, true);
    }
  },
  methods: {
    ...mapActions({ get: 'get', post: 'post', delete: 'delete' }),
    async loadTemplates() {
      const search = this.searchQuery?.trim() || ''
      const cacheKey = `${this.currentPage}|${this.perPage}|${search}|${this.templateFor || ''}`

      const cached = this.templatesCache[cacheKey]
      if (cached && Date.now() - cached.timestamp < this.cacheTTL) {
        this.templates = cached.data
        this.totalCount = cached.totalCount
        return
      }

      this.dataLoading = true
      this.currentRequestKey = cacheKey
      try {
        const params = new URLSearchParams({
          db: this.$store.state.userData.db,
          cID: this.$store.state.userData.cId,
          page: this.currentPage,
          pageSize: this.perPage,
        })

        if (search) {
          params.append('search', search)
        }
        
        if (this.templateFor) {
          params.append('templateFor', this.templateFor)
        }

        const obj = {
          url:
            this.$store.state.domain +
            'TemplateData/GetByPagination?' +
            params.toString(),
          token: this.$store.state.userData.token,
          dashboard: true,
        }
        const res = await this.get(obj)

        if (this.currentRequestKey !== cacheKey) {
          return
        }

        if (res && res.status === 'success' && Array.isArray(res.data)) {
          this.templates = res.data
          this.totalCount = res.totalCount || 0
        } else if (res && Array.isArray(res.data)) {
          this.templates = res.data
          this.totalCount = res.total || res.totalCount || 0
        } else {
          this.templates = []
          this.totalCount = 0
        }

        this.templatesCache[cacheKey] = {
          data: this.templates,
          totalCount: this.totalCount,
          timestamp: Date.now(),
        }
      } catch (error) {
        this.$bvToast.toast('Failed to load templates', {
          title: 'Error',
          variant: 'danger',
          solid: true,
        })
        this.templates = []
        this.totalCount = 0
      } finally {
        this.dataLoading = false
      }
    },
    onSearchChange() {
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout)
      }
      this.searchTimeout = setTimeout(() => {
        this.currentPage = 1
        this.loadTemplates()
      }, 500)
    },
    onTemplateForChange() {
      this.currentPage = 1
      this.loadTemplates()
    },
    onPageChange(page) {
      this.currentPage = page
      this.loadTemplates()
    },
    onPageSizeChange() {
      this.currentPage = 1
      this.loadTemplates()
    },
    closePageSizeDropdown() {
      this.$nextTick(() => {
        if (this.$refs.pageSizeSelect) {
          const toggle = this.$refs.pageSizeSelect.$el?.querySelector('.vs__dropdown-toggle')
          if (toggle) {
            toggle.blur()
            setTimeout(() => {
              document.body.click()
            }, 100)
          }
        }
      })
    },
    openAddModal() {
      this.isEdit = false
      this.form = {
        id: 0,
        title: '',
        format: '',
        templateFor: 'Student',
      }
      this.showModal = true
    },
    editTemplate(item) {
      this.editLoading = item.id
      this.isEdit = true
      
      // Convert RTF format to HTML for Quill editor if needed
      let formatValue = item.format || ''
      // Check if format is in RTF format (contains \rtf1, \b, \ul, etc.)
      if (formatValue && (formatValue.includes('\\rtf1') || formatValue.includes('\\b') || formatValue.includes('\\ul') || formatValue.includes('\\qc') || formatValue.includes('\\par'))) {
        formatValue = this.convertRtfToHtml(formatValue)
        console.log('Converted RTF to HTML:', formatValue) // Debug log
      } else if (formatValue && (formatValue.includes('[b]') || formatValue.includes('[i]') || formatValue.includes('[u]') || formatValue.includes('[s]') || formatValue.includes('[center]') || formatValue.includes('[right]') || formatValue.includes('[justify]') || formatValue.includes('[quote]') || formatValue.includes('[h') || formatValue.includes('{b}') || formatValue.includes('{i}'))) {
        // Legacy support: convert bracket notation to HTML
        formatValue = this.convertBracketsToHtml(formatValue)
      }
      
      this.form = {
        id: item.id,
        title: item.title || '',
        format: formatValue,
        templateFor: item.templateFor || 'Student',
      }
      this.showModal = true
      this.$nextTick(() => {
        this.editLoading = null
      })
    },
    async saveTemplate() {
      if (!this.isFormValid) {
        this.$bvToast.toast('Please fill in all required fields', {
          title: 'Validation Error',
          variant: 'warning',
          solid: true,
        })
        return
      }

      this.saving = true
      try {
        // Convert HTML format to RTF format before saving
        let formatValue = this.form.format || ''
        if (formatValue === null || formatValue === undefined) {
          formatValue = ''
        }
        
        // Convert HTML to Bracket format for saving
        const bracketFormat = this.convertHtmlToBrackets(formatValue)
        
        console.log('Saving Template - HTML:', formatValue)
        console.log('Saving Template - Bracket Format:', bracketFormat)

        const payload = {
          id: this.form.id,
          title: this.form.title.trim(),
          format: bracketFormat, // Save in Bracket format
          templateFor: this.form.templateFor,
          status: 'active', // Always set to active
        }

        const userID =
          this.$store.state.userData.userID ||
          this.$store.state.userData.userId ||
          this.$store.state.userData.id ||
          0

        const obj = {
          url:
            this.$store.state.domain +
            'TemplateData/Save?db=' +
            this.$store.state.userData.db +
            '&cID=' +
            this.$store.state.userData.cId +
            '&userID=' +
            userID,
          body: payload,
          token: this.$store.state.userData.token,
          message: this.isEdit
            ? 'Template updated successfully'
            : 'Template added successfully',
          context: this,
        }

        const success = await this.post(obj)
        if (success) {
          this.templatesCache = {}
          this.showModal = false
          this.loadTemplates()
        }
      } catch (error) {
        this.$bvToast.toast('Failed to save template', {
          title: 'Error',
          variant: 'danger',
          solid: true,
        })
      } finally {
        this.saving = false
      }
    },
    async deleteTemplate(item) {
      const result = await Swal.fire({
        title: 'Are you sure?',
        text: 'You won\'t be able to revert this!',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        cancelButtonColor: '#3085d6',
        confirmButtonText: 'Yes, delete it!',
      })

      if (result.isConfirmed) {
        this.deleteLoading = item.id
        try {
          const obj = {
            url:
              this.$store.state.domain +
              'TemplateData/Delete?' +
              'db=' +
              encodeURIComponent(this.$store.state.userData.db) +
              '&cID=' +
              this.$store.state.userData.cId +
              '&id=' +
              item.id,
            headers: {
              db: this.$store.state.userData.db,
            },
            token: this.$store.state.userData.token,
            message: 'Template deleted successfully',
            context: this,
          }

          const success = await this.delete(obj)
          if (success) {
            this.templatesCache = {}
            this.loadTemplates()
          }
        } catch (error) {
          this.$bvToast.toast('Failed to delete template', {
            title: 'Error',
            variant: 'danger',
            solid: true,
          })
        } finally {
          this.deleteLoading = null
        }
      }
    },
    resetForm() {
      this.isEdit = false
    },
    insertKeyword(keyword) {
      if (this.$refs.quillEditor) {
        const quill = this.$refs.quillEditor.quill
        const range = quill.getSelection()
        if (range) {
          quill.insertText(range.index, keyword)
          quill.setSelection(range.index + keyword.length)
        } else {
          // Focus the editor first if no selection
          quill.focus()
          const length = quill.getLength()
          if (length > 0) {
            quill.insertText(length - 1, keyword)
            quill.setSelection(length - 1 + keyword.length)
          } else {
            quill.insertText(0, keyword)
            quill.setSelection(keyword.length)
          }
        }
      }
    },
    viewTemplate(item) {
      // Convert format to plain text for display
      const plainText = this.getPlainTextFromFormat(item.format || '')
      this.viewData = {
        title: item.title || '',
        format: plainText,
        templateFor: item.templateFor || 'N/A',
      }
      this.showViewModal = true
    },
    resetViewModal() {
      this.viewData = {
        title: '',
        format: '',
        templateFor: '',
      }
    },
    getPlainText(html) {
      if (!html) return ''
      // Create a temporary div element
      const tempDiv = document.createElement('div')
      tempDiv.innerHTML = html
      // Get the text content and remove extra whitespace
      return tempDiv.textContent || tempDiv.innerText || ''
    },
    getBracketFormat(html) {
      if (!html) return ''
      
      // Create a temporary div element to parse HTML
      const tempDiv = document.createElement('div')
      tempDiv.innerHTML = html
      
      // Function to convert HTML to bracket notation
      const convertToBrackets = (node) => {
        if (node.nodeType === Node.TEXT_NODE) {
          return node.textContent || ''
        }
        
        if (node.nodeType !== Node.ELEMENT_NODE) {
          return ''
        }
        
        let result = ''
        const tagName = node.tagName ? node.tagName.toLowerCase() : ''
        const className = node.className || ''
        
        // Handle formatting tags
        let openTag = ''
        let closeTag = ''
        
        if (tagName === 'strong' || tagName === 'b') {
          openTag = '[b]'
          closeTag = '[/b]'
        } else if (tagName === 'em' || tagName === 'i') {
          openTag = '[i]'
          closeTag = '[/i]'
        } else if (tagName === 'u') {
          openTag = '[u]'
          closeTag = '[/u]'
        } else if (tagName === 's' || tagName === 'strike') {
          openTag = '[s]'
          closeTag = '[/s]'
        } else if (tagName === 'h1') {
          // Check for alignment classes
          if (className.includes('ql-align-center')) {
            openTag = '[center][h1]'
            closeTag = '[/h1][/center]'
          } else if (className.includes('ql-align-right')) {
            openTag = '[right][h1]'
            closeTag = '[/h1][/right]'
          } else if (className.includes('ql-align-justify')) {
            openTag = '[justify][h1]'
            closeTag = '[/h1][/justify]'
          } else {
            openTag = '[h1]'
            closeTag = '[/h1]'
          }
        } else if (tagName === 'h2') {
          // Check for alignment classes
          if (className.includes('ql-align-center')) {
            openTag = '[center][h2]'
            closeTag = '[/h2][/center]'
          } else if (className.includes('ql-align-right')) {
            openTag = '[right][h2]'
            closeTag = '[/h2][/right]'
          } else if (className.includes('ql-align-justify')) {
            openTag = '[justify][h2]'
            closeTag = '[/h2][/justify]'
          } else {
            openTag = '[h2]'
            closeTag = '[/h2]'
          }
        } else if (tagName === 'h3') {
          // Check for alignment classes
          if (className.includes('ql-align-center')) {
            openTag = '[center][h3]'
            closeTag = '[/h3][/center]'
          } else if (className.includes('ql-align-right')) {
            openTag = '[right][h3]'
            closeTag = '[/h3][/right]'
          } else if (className.includes('ql-align-justify')) {
            openTag = '[justify][h3]'
            closeTag = '[/h3][/justify]'
          } else {
            openTag = '[h3]'
            closeTag = '[/h3]'
          }
        } else if (tagName === 'h4') {
          // Check for alignment classes
          if (className.includes('ql-align-center')) {
            openTag = '[center][h4]'
            closeTag = '[/h4][/center]'
          } else if (className.includes('ql-align-right')) {
            openTag = '[right][h4]'
            closeTag = '[/h4][/right]'
          } else if (className.includes('ql-align-justify')) {
            openTag = '[justify][h4]'
            closeTag = '[/h4][/justify]'
          } else {
            openTag = '[h4]'
            closeTag = '[/h4]'
          }
        } else if (tagName === 'h5') {
          // Check for alignment classes
          if (className.includes('ql-align-center')) {
            openTag = '[center][h5]'
            closeTag = '[/h5][/center]'
          } else if (className.includes('ql-align-right')) {
            openTag = '[right][h5]'
            closeTag = '[/h5][/right]'
          } else if (className.includes('ql-align-justify')) {
            openTag = '[justify][h5]'
            closeTag = '[/h5][/justify]'
          } else {
            openTag = '[h5]'
            closeTag = '[/h5]'
          }
        } else if (tagName === 'h6') {
          // Check for alignment classes
          if (className.includes('ql-align-center')) {
            openTag = '[center][h6]'
            closeTag = '[/h6][/center]'
          } else if (className.includes('ql-align-right')) {
            openTag = '[right][h6]'
            closeTag = '[/h6][/right]'
          } else if (className.includes('ql-align-justify')) {
            openTag = '[justify][h6]'
            closeTag = '[/h6][/justify]'
          } else {
            openTag = '[h6]'
            closeTag = '[/h6]'
          }
        } else if (tagName === 'p') {
          // Check for alignment classes
          if (className.includes('ql-align-center')) {
            openTag = '[center]'
            closeTag = '[/center]'
          } else if (className.includes('ql-align-right')) {
            openTag = '[right]'
            closeTag = '[/right]'
          } else if (className.includes('ql-align-justify')) {
            openTag = '[justify]'
            closeTag = '[/justify]'
          } else {
            openTag = ''
            closeTag = '\n'
          }
        } else if (tagName === 'br') {
          return '\n'
        } else if (tagName === 'ul' || tagName === 'ol') {
          openTag = ''
          closeTag = '\n'
        } else if (tagName === 'li') {
          openTag = '• '
          closeTag = '\n'
        } else if (tagName === 'blockquote') {
          openTag = '[quote]'
          closeTag = '[/quote]'
        }
        
        // Process child nodes
        let content = ''
        if (node.childNodes && node.childNodes.length > 0) {
          content = Array.from(node.childNodes)
            .map(child => convertToBrackets(child))
            .join('')
        }
        
        // Remove trailing newline from content if closeTag starts with newline
        if (closeTag.startsWith('\n') && content.endsWith('\n')) {
          content = content.slice(0, -1)
        }
        
        result = openTag + content + closeTag
        
        return result
      }
      
      // Convert the HTML to bracket notation
      let bracketText = convertToBrackets(tempDiv)
      
      // Clean up multiple newlines
      bracketText = bracketText.replace(/\n{3,}/g, '\n\n')
      
      // Trim whitespace
      bracketText = bracketText.trim()
      
      return bracketText
    },
    // Convert HTML to bracket notation for saving
    convertHtmlToBrackets(html) {
      if (!html) return ''
      // Use the existing getBracketFormat function
      return this.getBracketFormat(html)
    },
    // Convert bracket notation back to HTML for Quill editor
    convertBracketsToHtml(bracketText) {
      if (!bracketText) return ''
      
      let html = bracketText
      
      // Use a more robust approach: process tags recursively to handle nesting
      // Process from outermost to innermost structure
      
      // Handle headings with alignment and nested formatting (most complex first)
      // Center aligned headings
      html = html.replace(/\[center\]\[h1\]([\s\S]*?)\[\/h1\]\[\/center\]/g, (match, content) => {
        return '<h1 class="ql-align-center">' + this.processNestedBrackets(content) + '</h1>'
      })
      html = html.replace(/\[center\]\[h2\]([\s\S]*?)\[\/h2\]\[\/center\]/g, (match, content) => {
        return '<h2 class="ql-align-center">' + this.processNestedBrackets(content) + '</h2>'
      })
      html = html.replace(/\[center\]\[h3\]([\s\S]*?)\[\/h3\]\[\/center\]/g, (match, content) => {
        return '<h3 class="ql-align-center">' + this.processNestedBrackets(content) + '</h3>'
      })
      html = html.replace(/\[center\]\[h4\]([\s\S]*?)\[\/h4\]\[\/center\]/g, (match, content) => {
        return '<h4 class="ql-align-center">' + this.processNestedBrackets(content) + '</h4>'
      })
      html = html.replace(/\[center\]\[h5\]([\s\S]*?)\[\/h5\]\[\/center\]/g, (match, content) => {
        return '<h5 class="ql-align-center">' + this.processNestedBrackets(content) + '</h5>'
      })
      html = html.replace(/\[center\]\[h6\]([\s\S]*?)\[\/h6\]\[\/center\]/g, (match, content) => {
        return '<h6 class="ql-align-center">' + this.processNestedBrackets(content) + '</h6>'
      })
      
      // Right aligned headings
      html = html.replace(/\[right\]\[h1\]([\s\S]*?)\[\/h1\]\[\/right\]/g, (match, content) => {
        return '<h1 class="ql-align-right">' + this.processNestedBrackets(content) + '</h1>'
      })
      html = html.replace(/\[right\]\[h2\]([\s\S]*?)\[\/h2\]\[\/right\]/g, (match, content) => {
        return '<h2 class="ql-align-right">' + this.processNestedBrackets(content) + '</h2>'
      })
      html = html.replace(/\[right\]\[h3\]([\s\S]*?)\[\/h3\]\[\/right\]/g, (match, content) => {
        return '<h3 class="ql-align-right">' + this.processNestedBrackets(content) + '</h3>'
      })
      html = html.replace(/\[right\]\[h4\]([\s\S]*?)\[\/h4\]\[\/right\]/g, (match, content) => {
        return '<h4 class="ql-align-right">' + this.processNestedBrackets(content) + '</h4>'
      })
      html = html.replace(/\[right\]\[h5\]([\s\S]*?)\[\/h5\]\[\/right\]/g, (match, content) => {
        return '<h5 class="ql-align-right">' + this.processNestedBrackets(content) + '</h5>'
      })
      html = html.replace(/\[right\]\[h6\]([\s\S]*?)\[\/h6\]\[\/right\]/g, (match, content) => {
        return '<h6 class="ql-align-right">' + this.processNestedBrackets(content) + '</h6>'
      })
      
      // Justify aligned headings
      html = html.replace(/\[justify\]\[h1\]([\s\S]*?)\[\/h1\]\[\/justify\]/g, (match, content) => {
        return '<h1 class="ql-align-justify">' + this.processNestedBrackets(content) + '</h1>'
      })
      html = html.replace(/\[justify\]\[h2\]([\s\S]*?)\[\/h2\]\[\/justify\]/g, (match, content) => {
        return '<h2 class="ql-align-justify">' + this.processNestedBrackets(content) + '</h2>'
      })
      html = html.replace(/\[justify\]\[h3\]([\s\S]*?)\[\/h3\]\[\/justify\]/g, (match, content) => {
        return '<h3 class="ql-align-justify">' + this.processNestedBrackets(content) + '</h3>'
      })
      html = html.replace(/\[justify\]\[h4\]([\s\S]*?)\[\/h4\]\[\/justify\]/g, (match, content) => {
        return '<h4 class="ql-align-justify">' + this.processNestedBrackets(content) + '</h4>'
      })
      html = html.replace(/\[justify\]\[h5\]([\s\S]*?)\[\/h5\]\[\/justify\]/g, (match, content) => {
        return '<h5 class="ql-align-justify">' + this.processNestedBrackets(content) + '</h5>'
      })
      html = html.replace(/\[justify\]\[h6\]([\s\S]*?)\[\/h6\]\[\/justify\]/g, (match, content) => {
        return '<h6 class="ql-align-justify">' + this.processNestedBrackets(content) + '</h6>'
      })
      
      // Handle simple headings (without alignment)
      html = html.replace(/\[h1\]([\s\S]*?)\[\/h1\]/g, (match, content) => {
        return '<h1>' + this.processNestedBrackets(content) + '</h1>'
      })
      html = html.replace(/\[h2\]([\s\S]*?)\[\/h2\]/g, (match, content) => {
        return '<h2>' + this.processNestedBrackets(content) + '</h2>'
      })
      html = html.replace(/\[h3\]([\s\S]*?)\[\/h3\]/g, (match, content) => {
        return '<h3>' + this.processNestedBrackets(content) + '</h3>'
      })
      html = html.replace(/\[h4\]([\s\S]*?)\[\/h4\]/g, (match, content) => {
        return '<h4>' + this.processNestedBrackets(content) + '</h4>'
      })
      html = html.replace(/\[h5\]([\s\S]*?)\[\/h5\]/g, (match, content) => {
        return '<h5>' + this.processNestedBrackets(content) + '</h5>'
      })
      html = html.replace(/\[h6\]([\s\S]*?)\[\/h6\]/g, (match, content) => {
        return '<h6>' + this.processNestedBrackets(content) + '</h6>'
      })
      
      // Handle alignment for paragraphs and other content
      html = html.replace(/\[center\]([\s\S]*?)\[\/center\]/g, (match, content) => {
        return '<p class="ql-align-center">' + this.processNestedBrackets(content) + '</p>'
      })
      html = html.replace(/\[right\]([\s\S]*?)\[\/right\]/g, (match, content) => {
        return '<p class="ql-align-right">' + this.processNestedBrackets(content) + '</p>'
      })
      html = html.replace(/\[justify\]([\s\S]*?)\[\/justify\]/g, (match, content) => {
        return '<p class="ql-align-justify">' + this.processNestedBrackets(content) + '</p>'
      })
      
      // Handle other tags
      html = html.replace(/\[quote\]([\s\S]*?)\[\/quote\]/g, (match, content) => {
        return '<blockquote>' + this.processNestedBrackets(content) + '</blockquote>'
      })
      
      // Process any remaining nested formatting tags
      html = this.processNestedBrackets(html)
      
      // Handle line breaks
      html = html.replace(/\n/g, '<br>')
      
      // Wrap in paragraph if not already wrapped
      if (html && !html.trim().startsWith('<')) {
        html = '<p>' + html + '</p>'
      }
      
      return html
    },
    // Helper function to process nested bracket formatting tags
    processNestedBrackets(text) {
      if (!text) return ''
      
      let result = text
      
      // Process formatting tags (can be nested)
      // Use a recursive approach to handle deeply nested tags
      let changed = true
      let iterations = 0
      const maxIterations = 10 // Prevent infinite loops
      
      while (changed && iterations < maxIterations) {
        const before = result
        result = result.replace(/\[b\]([\s\S]*?)\[\/b\]/g, '<strong>$1</strong>')
        result = result.replace(/\[i\]([\s\S]*?)\[\/i\]/g, '<em>$1</em>')
        result = result.replace(/\[u\]([\s\S]*?)\[\/u\]/g, '<u>$1</u>')
        result = result.replace(/\[s\]([\s\S]*?)\[\/s\]/g, '<s>$1</s>')
        changed = (before !== result)
        iterations++
      }
      
      return result
    },
    // Convert HTML to RTF format for saving
    convertHtmlToRtf(html) {
      if (!html) return ''
      
      // Create a temporary div element to parse HTML
      const tempDiv = document.createElement('div')
      tempDiv.innerHTML = html
      
      // RTF header
      let rtf = '{\\rtf1\\ansi'
      
      // Function to convert HTML node to RTF
      const convertToRtf = (node, parentAlignment = '') => {
        if (node.nodeType === Node.TEXT_NODE) {
          // Escape RTF special characters in text
          let text = node.textContent || ''
          text = text.replace(/\\/g, '\\\\')
          text = text.replace(/\{/g, '\\{')
          text = text.replace(/\}/g, '\\}')
          return text
        }
        
        if (node.nodeType !== Node.ELEMENT_NODE) {
          return ''
        }
        
        const tagName = node.tagName ? node.tagName.toLowerCase() : ''
        const className = node.className || ''
        
        // Determine alignment for this element
        let alignment = parentAlignment
        if (className.includes('ql-align-center')) {
          alignment = '\\qc'
        } else if (className.includes('ql-align-right')) {
          alignment = '\\qr'
        } else if (className.includes('ql-align-justify')) {
          alignment = '\\qj'
        } else if (tagName === 'h1' || tagName === 'h2' || tagName === 'h3' || tagName === 'h4' || tagName === 'h5' || tagName === 'h6' || tagName === 'p') {
          // Use parent alignment or default to left
          if (!alignment) alignment = '\\ql'
        }
        
        // Process child nodes - track formatting to avoid duplicates
        const processChild = (child, currentFormatting = {}) => {
          if (child.nodeType === Node.TEXT_NODE) {
            // Escape RTF special characters in text
            let text = child.textContent || ''
            text = text.replace(/\\/g, '\\\\')
            text = text.replace(/\{/g, '\\{')
            text = text.replace(/\}/g, '\\}')
            return text
          }
          
          if (child.nodeType !== Node.ELEMENT_NODE) {
            return ''
          }
          
          const childTag = child.tagName ? child.tagName.toLowerCase() : ''
          const newFormatting = { ...currentFormatting }
          let openTags = ''
          let closeTags = ''
          
          // Only add formatting if not already active
          if (childTag === 'strong' || childTag === 'b') {
            if (!newFormatting.bold) {
              openTags = '\\b '
              closeTags = ' \\b0' + closeTags
              newFormatting.bold = true
            }
          } else if (childTag === 'em' || childTag === 'i') {
            if (!newFormatting.italic) {
              openTags = '\\i '
              closeTags = ' \\i0' + closeTags
              newFormatting.italic = true
            }
          } else if (childTag === 'u') {
            if (!newFormatting.underline) {
              openTags = '\\ul '
              closeTags = ' \\ulnone' + closeTags
              newFormatting.underline = true
            }
          } else if (childTag === 's' || childTag === 'strike') {
            if (!newFormatting.strike) {
              openTags = '\\strike '
              closeTags = ' \\strike0' + closeTags
              newFormatting.strike = true
            }
          }
          
          const innerContent = processChildren(child, newFormatting)
          return openTags + innerContent + closeTags
        }
        
        const processChildren = (parentNode, currentFormatting = {}) => {
          if (!parentNode.childNodes || parentNode.childNodes.length === 0) {
            return ''
          }
          return Array.from(parentNode.childNodes)
            .map(child => processChild(child, currentFormatting))
            .join('')
        }
        
        textContent = processChildren(node)
        
        // Build RTF content based on tag type
        let rtfContent = ''
        
        if (tagName === 'h1' || tagName === 'h2' || tagName === 'h3' || tagName === 'h4' || tagName === 'h5' || tagName === 'h6') {
          // Headings: alignment + content + paragraph break
          rtfContent = alignment + ' ' + textContent + '\\par'
        } else if (tagName === 'p') {
          // Paragraphs: alignment + content + paragraph break
          rtfContent = alignment + ' ' + textContent + '\\par'
        } else if (tagName === 'br') {
          rtfContent = '\\line'
        } else if (tagName === 'strong' || tagName === 'b') {
          rtfContent = '\\b ' + textContent + ' \\b0'
        } else if (tagName === 'em' || tagName === 'i') {
          rtfContent = '\\i ' + textContent + ' \\i0'
        } else if (tagName === 'u') {
          rtfContent = '\\ul ' + textContent + ' \\ulnone'
        } else if (tagName === 's' || tagName === 'strike') {
          rtfContent = '\\strike ' + textContent + ' \\strike0'
        } else if (tagName === 'blockquote') {
          rtfContent = '\\ql ' + textContent + '\\par'
        } else {
          // For other tags, just process content
          rtfContent = textContent
        }
        
        return rtfContent
      }
      
      // Convert all nodes
      let bodyContent = ''
      if (tempDiv.childNodes && tempDiv.childNodes.length > 0) {
        bodyContent = Array.from(tempDiv.childNodes)
          .map(child => convertToRtf(child))
          .join('')
      }
      
      // Close RTF
      rtf += bodyContent + '}'
      
      return rtf
    },
    // Convert RTF format back to HTML for Quill editor
    convertRtfToHtml(rtf) {
      if (!rtf) return ''
      
      // Remove RTF header and footer
      let content = rtf.trim()
      if (content.startsWith('{')) {
        content = content.substring(1)
      }
      if (content.endsWith('}')) {
        content = content.slice(0, -1)
      }
      
      // Remove RTF header commands
      content = content.replace(/^\\rtf1\\ansi\s*/, '')
      
      let html = ''
      let currentAlignment = 'ql-align-left'
      let inBold = false
      let inItalic = false
      let inUnderline = false
      let inStrike = false
      
      // Better RTF parsing - handle commands and text separately
      let i = 0
      while (i < content.length) {
        if (content[i] === '\\') {
          // Parse RTF command
          let command = ''
          i++ // Skip backslash
          
          // Get command name (until space, number, or end)
          while (i < content.length && /[a-zA-Z]/.test(content[i])) {
            command += content[i]
            i++
          }
          
          // Skip numbers after command (like \b1, \ul2, etc.)
          while (i < content.length && /\d/.test(content[i])) {
            i++
          }
          
          // Skip space after command
          if (i < content.length && content[i] === ' ') {
            i++
          }
          
          // Process command
          if (command === 'qc') {
            currentAlignment = 'ql-align-center'
          } else if (command === 'qr') {
            currentAlignment = 'ql-align-right'
          } else if (command === 'qj') {
            currentAlignment = 'ql-align-justify'
          } else if (command === 'ql') {
            currentAlignment = 'ql-align-left'
          } else if (command === 'b') {
            if (!inBold) {
              html += '<strong>'
              inBold = true
            }
          } else if (command === 'b0') {
            if (inBold) {
              html += '</strong>'
              inBold = false
            }
          } else if (command === 'i') {
            if (!inItalic) {
              html += '<em>'
              inItalic = true
            }
          } else if (command === 'i0') {
            if (inItalic) {
              html += '</em>'
              inItalic = false
            }
          } else if (command === 'ul') {
            if (!inUnderline) {
              html += '<u>'
              inUnderline = true
            }
          } else if (command === 'ulnone') {
            if (inUnderline) {
              html += '</u>'
              inUnderline = false
            }
          } else if (command === 'strike') {
            if (!inStrike) {
              html += '<s>'
              inStrike = true
            }
          } else if (command === 'strike0') {
            if (inStrike) {
              html += '</s>'
              inStrike = false
            }
          } else if (command === 'par') {
            // Close any open formatting
            if (inStrike) {
              html += '</s>'
              inStrike = false
            }
            if (inUnderline) {
              html += '</u>'
              inUnderline = false
            }
            if (inItalic) {
              html += '</em>'
              inItalic = false
            }
            if (inBold) {
              html += '</strong>'
              inBold = false
            }
            html += `<p class="${currentAlignment}"><br></p>`
          } else if (command === 'line') {
            html += '<br>'
          }
          // Skip other commands
        } else {
          // Regular text
          let text = ''
          while (i < content.length && content[i] !== '\\') {
            // Unescape RTF special characters
            if (content[i] === '{' || content[i] === '}') {
              // Skip braces (they're RTF structure)
              i++
              continue
            }
            text += content[i]
            i++
          }
          
          if (text.trim()) {
            // Unescape RTF escapes
            text = text.replace(/\\\\/g, '\\')
            html += text
          }
        }
      }
      
      // Close any remaining open tags
      if (inStrike) html += '</s>'
      if (inUnderline) html += '</u>'
      if (inItalic) html += '</em>'
      if (inBold) html += '</strong>'
      
      // If no HTML tags, wrap in paragraph
      if (html && !html.trim().startsWith('<')) {
        html = `<p class="${currentAlignment}">${html}</p>`
      }
      
      // Clean up empty paragraphs
      html = html.replace(/<p[^>]*><br><\/p>/g, '<p><br></p>')
      
      return html
    },
    // Extract plain text from format (handles RTF, HTML, or bracket notation)
    getPlainTextFromFormat(format) {
      if (!format) return ''
      
      // Check if it's RTF format
      if (format.includes('\\rtf1') || format.includes('\\b') || format.includes('\\ul') || format.includes('\\qc') || format.includes('\\par')) {
        return this.extractPlainTextFromRtf(format)
      }
      
      // Check if it's HTML format
      if (format.includes('<') && format.includes('>')) {
        return this.getPlainText(format)
      }
      
      // Check if it's bracket notation
      if (format.includes('[b]') || format.includes('[i]') || format.includes('[u]') || format.includes('[s]') || format.includes('[center]') || format.includes('[right]') || format.includes('[justify]') || format.includes('[quote]') || format.includes('[h') || format.includes('{b}') || format.includes('{i}')) {
        // Convert bracket to HTML first, then extract text
        const html = this.convertBracketsToHtml(format)
        return this.getPlainText(html)
      }
      
      // Already plain text
      return format
    },
    // Extract plain text from RTF format
    extractPlainTextFromRtf(rtf) {
      if (!rtf) return ''
      
      // Remove RTF header and footer
      let content = rtf.trim()
      if (content.startsWith('{')) {
        content = content.substring(1)
      }
      if (content.endsWith('}')) {
        content = content.slice(0, -1)
      }
      
      // Remove RTF header commands
      content = content.replace(/^\\rtf1\\ansi\s*/, '')
      
      let text = ''
      let i = 0
      
      while (i < content.length) {
        if (content[i] === '\\') {
          // Skip RTF command
          i++ // Skip backslash
          
          // Get command name
          let command = ''
          while (i < content.length && /[a-zA-Z]/.test(content[i])) {
            command += content[i]
            i++
          }
          
          // Skip numbers after command
          while (i < content.length && /\d/.test(content[i])) {
            i++
          }
          
          // Skip space after command
          if (i < content.length && content[i] === ' ') {
            i++
          }
          
          // Commands that don't produce text - just skip
          // par, line, qc, ql, qr, qj, b, b0, i, i0, ul, ulnone, strike, strike0, etc.
        } else {
          // Regular text
          let char = content[i]
          if (char !== '{' && char !== '}') {
            // Unescape RTF escapes
            if (char === '\\' && i + 1 < content.length) {
              const next = content[i + 1]
              if (next === '\\' || next === '{' || next === '}') {
                text += next
                i += 2
                continue
              }
            }
            text += char
          }
          i++
        }
      }
      
      // Clean up extra spaces and newlines
      text = text.replace(/\s+/g, ' ').trim()
      
      return text
    },
  },
}
</script>

<style lang="scss" scoped>
@import "@core/scss/vue/libs/vue-select.scss";
@import "@core/scss/vue/libs/quill.scss";

/* Keyframe animations - Soft landing */
@keyframes slideUpFade {
  from {
    opacity: 0;
    transform: translateY(15px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.filter-row-single-line {
  gap: 0.5rem;
  flex-wrap: wrap;
  margin-left: -0.25rem;
  margin-right: -0.25rem;
  align-items: center;
  background: transparent !important;

  @media (min-width: 1200px) {
    flex-wrap: nowrap;
  }

  > [class*="col-"] {
    padding-left: 0.375rem;
    padding-right: 0.375rem;
    display: flex;
    align-items: center;
    background: transparent !important;
  }

  > [class*="col-"].text-right {
    justify-content: flex-end;
  }
}

.filter-control {
  height: 42px !important;
  min-height: 42px !important;
  border-radius: 10px;
  border: 1.5px solid #e0d8ff;
  padding: 0.5rem 0.75rem;
  background: #fff;
  transition: all 0.2s ease;
  color: #2d1b69;
  font-weight: 500;
  width: 100%;

  &:hover {
    border-color: #b8a9ff;
  }

  &:focus {
    border-color: #7c5dfa;
    box-shadow: 0 0 0 3px rgba(124, 93, 250, 0.1);
    outline: none;
  }

  &::placeholder {
    color: #9ca3af;
    font-weight: 400;
  }
}

.filter-select {
  width: 100%;

  ::v-deep .vs__dropdown-toggle {
    height: 42px !important;
    min-height: 42px !important;
    border-radius: 10px;
    border: 1.5px solid #e0d8ff !important;
    padding: 0.25rem 0.5rem !important;
    background: #fff !important;
    transition: all 0.2s ease;
    
    &:hover {
      border-color: #b8a9ff !important;
    }
  }

  /* Focus state */
  ::v-deep .vs__dropdown-toggle:focus-within {
    border-color: #7c5dfa !important;
    box-shadow: 0 0 0 3px rgba(124, 93, 250, 0.1) !important;
  }
  
  /* Selected text color */
  ::v-deep .vs__selected {
    color: #2d1b69 !important;
    font-weight: 500;
    margin-top: 3px;
  }

  ::v-deep .vs__search::placeholder {
     color: #9ca3af;
  }

  ::v-deep .vs__open-indicator {
    fill: #64748b;
  }
}

.action-btn-filter {
  height: 42px;
  font-weight: 600;
  border-radius: 10px;
  padding: 0.5rem 1rem;
  transition: all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
}

.gap-2 {
  gap: 0.5rem;
}

.templates-list-card {
  background: linear-gradient(180deg, #ffffff 0%, #faf9ff 100%);
  border: 1px solid #e0d8ff;
  border-radius: 20px;
  padding: 1.5rem 1.75rem 1.5rem 1.5rem;
  box-shadow: 0 10px 30px rgba(115, 103, 240, 0.12);
  margin-bottom: 1.5rem;
  transition: all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  position: relative;
  overflow: visible;
  animation: slideUpFade 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94) both;
  z-index: 1;

  .gradient-line-wrapper {
    position: absolute;
    top: 0;
    left: 0.75rem;
    right: 0.75rem;
    width: calc(100% - 1.5rem);
    height: 5px;
    overflow: hidden;
    border-radius: 10px 10px 0 0;
    z-index: 1;
    pointer-events: none;
    
    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      width: 100%;
      height: 5px;
      background: linear-gradient(90deg, #7c5dfa 0%, #9d7aff 50%, #cbb9ff 100%);
      border-radius: 10px 10px 0 0;
    }
  }

  &:hover {
    box-shadow: 0 15px 40px rgba(115, 103, 240, 0.2);
    transform: translateY(-2px);
  }
  
  .filter-row-single-line {
    background: transparent !important;
    padding: 0;
    margin-bottom: 1rem;
  }
}

.table-header {
  padding-bottom: 1rem;
  border-bottom: 2px solid #f0edff;
  margin-bottom: 1rem;

  h5 {
    font-size: 1.25rem;
    font-weight: 800;
    color: #2d1b69;
    letter-spacing: -0.3px;
  }

  small {
    font-size: 0.85rem;
    color: #6b7280;
    font-weight: 500;
  }
}

.template-title {
  font-size: 1.25rem;
  font-weight: 800;
  color: #2d1b69;
  letter-spacing: -0.3px;
}

.pagination-controls-top {
  padding: 0.75rem 1.25rem;
  background: linear-gradient(180deg, #faf9ff 0%, #ffffff 100%);
  border: 1px solid #e0d8ff;
  border-radius: 10px;
  margin-bottom: 1rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: nowrap;
  gap: 1rem;
  position: relative;
  
  @media (max-width: 992px) {
    flex-wrap: wrap;
    gap: 0.75rem;
    
    .pagination-count-display {
      order: 3;
      width: 100%;
      margin: 0.5rem 0 0 0;
      text-align: center;
    }
  }
  
  > .d-flex {
    gap: 0.5rem;
    flex-wrap: nowrap;
    align-items: center;
    flex-shrink: 0;
  }
  
  > div {
    flex-shrink: 0;
    display: flex;
    align-items: center;
  }
}

.page-size-wrapper {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  white-space: nowrap;
  
  .page-size-label {
    font-size: 0.8125rem;
    font-weight: 600;
    color: #64748b;
    margin: 0;
    white-space: nowrap;
  }
}

.page-size-select-compact {
  min-width: 75px;
  width: 75px;
  
  ::v-deep .vs__dropdown-toggle {
    height: 32px !important;
    min-height: 32px !important;
    border-radius: 6px !important;
    border: 1.5px solid #e0d8ff !important;
    padding: 0.25rem 0.5rem !important;
    background: #fff !important;
    font-size: 0.8125rem !important;
    
    &:hover {
      border-color: #b8a9ff !important;
    }
    
    &:focus-within {
      border-color: #7c5dfa !important;
      box-shadow: 0 0 0 2px rgba(124, 93, 250, 0.1) !important;
    }
  }
  
  ::v-deep .vs__selected {
    margin: 0 !important;
    padding: 0 !important;
    color: #2d1b69 !important;
    font-weight: 600 !important;
    font-size: 0.8125rem !important;
    line-height: 1.5 !important;
    display: block !important;
    visibility: visible !important;
    opacity: 1 !important;
  }
  
  ::v-deep .vs__selected-options {
    padding: 0 !important;
    display: flex !important;
    align-items: center !important;
  }
  
  ::v-deep .vs__search {
    display: none !important;
    width: 0 !important;
    height: 0 !important;
    margin: 0 !important;
    padding: 0 !important;
  }
  
  ::v-deep .vs__actions {
    padding: 0 0.25rem 0 0.5rem !important;
  }
  
  ::v-deep .vs__dropdown-menu {
    z-index: 99999 !important;
    position: fixed !important;
    min-width: 75px !important;
    width: 75px !important;
    border-radius: 8px !important;
    border: 1.5px solid #e0d8ff !important;
    box-shadow: 0 4px 12px rgba(115, 103, 240, 0.15) !important;
    margin-top: 4px !important;
    background: #ffffff !important;
    overflow: visible !important;
    max-height: 300px !important;
    overflow-y: auto !important;
  }
  
  ::v-deep .vs__dropdown-option {
    padding: 0.5rem 0.75rem !important;
    color: #2d1b69 !important;
    font-weight: 500 !important;
    font-size: 0.8125rem !important;
    background-color: #ffffff !important;
    background-image: none !important;
    opacity: 1 !important;
    visibility: visible !important;
    cursor: pointer !important;
    transition: background-color 0.15s ease !important;
    
    &:hover {
      background-color: #f9fafb !important;
      background-image: none !important;
      color: #1f2937 !important;
    }
    
    &--highlight,
    &--selected {
      background-color: #f3f4f6 !important;
      background-image: none !important;
      color: #1f2937 !important;
      font-weight: 600 !important;
    }
    
    &:active {
      background-color: #e5e7eb !important;
    }
    
    &--selected {
      background-color: #f3f0ff !important;
      color: #7c5dfa !important;
      font-weight: 600 !important;
    }
  }
}

.pagination-count-display {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;
  margin: 0 1rem;
  min-width: 200px;
  
  .count-text {
    font-size: 0.8125rem;
    color: #64748b;
    font-weight: 500;
    white-space: nowrap;
    
    strong {
      color: #2d1b69;
      font-weight: 700;
    }
  }
  
  @media (max-width: 992px) {
    margin: 0;
    min-width: auto;
    width: 100%;
  }
}

.pagination-wrapper-compact {
  display: flex;
  align-items: center;
  
  ::v-deep .pagination {
    margin-bottom: 0;
    
    .page-link {
      padding: 0.375rem 0.625rem;
      font-size: 0.8125rem;
      line-height: 1.5;
      border-radius: 6px;
      margin: 0 0.125rem;
      border: 1px solid #e0d8ff;
      color: #64748b;
      font-weight: 500;
      min-width: 32px;
      height: 32px;
      display: flex;
      align-items: center;
      justify-content: center;
      
      &:hover {
        background: #f3f0ff;
        border-color: #b8a9ff;
        color: #7c5dfa;
      }
    }
    
    .page-item.active .page-link {
      background: linear-gradient(135deg, #7c5dfa 0%, #9d7aff 100%);
      border-color: #7c5dfa;
      color: #fff;
      font-weight: 600;
      box-shadow: 0 2px 8px rgba(124, 93, 250, 0.3);
    }
    
    .page-item.disabled .page-link {
      background: #f8fafc;
      border-color: #e0d8ff;
      color: #cbd5e1;
      cursor: not-allowed;
    }
  }
}

.templates-table {
  margin-top: 1rem;
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid #e5e7eb;

  ::v-deep th {
    background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
    border: none;
    font-weight: 700;
    color: #1e293b;
    text-transform: uppercase;
    font-size: 0.7rem;
    letter-spacing: 0.5px;
    padding: 0.75rem 0.875rem;
    border-bottom: 1px solid #e2e8f0;
    white-space: nowrap;
    position: sticky;
    top: 0;
    z-index: 5;

    &::after {
      display: none;
    }
  }

  ::v-deep td {
    vertical-align: middle;
    border-top: 1px solid #f1f5f9;
    border-bottom: 1px solid #f8fafc;
    padding: 0.75rem 0.875rem;
    background: #ffffff;
    transition: all 0.2s cubic-bezier(0.25, 0.46, 0.45, 0.94);
    font-size: 0.875rem;
    color: #475569;
    
    &.template-cell {
      vertical-align: middle !important;
      white-space: normal !important;
      word-break: break-word !important;
      word-wrap: break-word !important;
      
      ::v-deep * {
        margin: 0;
        padding: 0;
      }
    }
  }

  ::v-deep tbody tr {
    transition: all 0.2s cubic-bezier(0.25, 0.46, 0.45, 0.94);
    position: relative;
    border-left: 2px solid transparent;

    &:nth-child(even) {
      background: #f8fafc;
      
      td {
        background: #f8fafc;
      }
    }

    &:nth-child(odd) {
      background: #ffffff;
      
      td {
        background: #ffffff;
      }
    }

    &:hover {
      background: #f1f5f9 !important;
      border-left-color: #7c5dfa;
      box-shadow: -2px 0 8px rgba(124, 93, 250, 0.1);
      z-index: 1;
      position: relative;

      td {
        background: transparent !important;
        color: #1e293b;
        font-weight: 500;
      }
    }

    &:active {
      transform: none;
    }
  }
}

.skeleton-row {
  animation: skeletonPulse 2s ease-in-out infinite;
  transition: opacity 0.3s ease;
  
  &:hover {
    background: transparent !important;
    border-left-color: transparent !important;
    box-shadow: none !important;
    transform: none !important;
    
    td {
      background: transparent !important;
      color: inherit !important;
      font-weight: normal !important;
    }
  }
}

.skeleton-cell {
  padding: 0.875rem 0.875rem !important;
  vertical-align: middle !important;
  border-top: 1px solid #f1f5f9 !important;
  border-bottom: 1px solid #f8fafc !important;
  background: #ffffff !important;
  position: relative;
}

.skeleton-loader {
  height: 18px;
  border-radius: 10px;
  background: linear-gradient(
    90deg,
    #f0f0f0 0%,
    #f8f8f8 25%,
    #f0f0f0 50%,
    #f8f8f8 75%,
    #f0f0f0 100%
  );
  background-size: 300% 100%;
  animation: skeletonShimmer 2s ease-in-out infinite;
  position: relative;
  overflow: hidden;
  width: 100%;
  max-width: 100%;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

@keyframes skeletonShimmer {
  0% {
    background-position: -300% 0;
  }
  100% {
    background-position: 300% 0;
  }
}

@keyframes skeletonPulse {
  0%, 100% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.85;
    transform: scale(0.998);
  }
}

.actions-cell {
  min-width: 150px;
  max-width: 180px;
  vertical-align: middle !important;
}

.template-cell-truncated {
  max-width: 400px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: #475569;
  font-size: 0.875rem;
  line-height: 1.5;
}

.action-btn-view {
  width: 36px;
  height: 36px;
  padding: 0;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  font-weight: 600;
  background: linear-gradient(135deg, #7c5dfa 0%, #9d7aff 100%) !important;
  border: none !important;
  color: #ffffff !important;
  box-shadow: 0 2px 8px rgba(124, 93, 250, 0.3);
  
  ::v-deep svg {
    color: #ffffff !important;
    stroke: #ffffff !important;
  }
  
  &:hover:not(:disabled) {
    background: linear-gradient(135deg, #6d4cdb 0%, #8b6fff 100%) !important;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(124, 93, 250, 0.4);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
}

.view-template-content {
  .view-field-value {
    padding: 0.75rem 1rem;
    background: #f8fafc;
    border-radius: 8px;
    border: 1px solid #e0d8ff;
    color: #1e293b;
    font-weight: 500;
    font-size: 0.9375rem;
    min-height: 2.5rem;
    display: flex;
    align-items: center;
  }

  .view-format-content {
    padding: 1rem;
    background: #ffffff;
    border-radius: 8px;
    border: 1px solid #e0d8ff;
    color: #1e293b;
    font-size: 0.9375rem;
    line-height: 1.6;
    min-height: 200px;
    max-height: 400px;
    overflow-y: auto;
    
    ::v-deep * {
      margin: 0;
      padding: 0;
    }
    
    ::v-deep p {
      margin-bottom: 0.75rem;
      
      &:last-child {
        margin-bottom: 0;
      }
    }
    
    ::v-deep h1, ::v-deep h2, ::v-deep h3, ::v-deep h4, ::v-deep h5, ::v-deep h6 {
      margin-bottom: 0.5rem;
      margin-top: 1rem;
      
      &:first-child {
        margin-top: 0;
      }
    }
    
    ::v-deep ul, ::v-deep ol {
      margin-left: 1.5rem;
      margin-bottom: 0.75rem;
    }
    
    ::v-deep blockquote {
      border-left: 4px solid #7c5dfa;
      padding-left: 1rem;
      margin: 1rem 0;
      font-style: italic;
      color: #64748b;
    }
  }
}

.action-btn-table {
  width: 36px;
  height: 36px;
  padding: 0;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  font-weight: 600;
  background: #ffffff !important;
  border: 2px solid #10b981 !important;
  color: #10b981 !important;
  box-shadow: 0 2px 8px rgba(16, 185, 129, 0.15);
  
  ::v-deep svg {
    color: #10b981 !important;
    stroke: #10b981 !important;
  }
  
  &:hover:not(:disabled) {
    background: #f0fdf4 !important;
    border-color: #059669 !important;
    color: #059669 !important;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(16, 185, 129, 0.25);
    
    ::v-deep svg {
      color: #059669 !important;
      stroke: #059669 !important;
    }
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
}

.action-btn-outline {
  width: 36px;
  height: 36px;
  padding: 0;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  font-weight: 600;
  background: #ffffff !important;
  border: 2px solid #ef4444 !important;
  color: #ef4444 !important;
  box-shadow: 0 2px 8px rgba(239, 68, 68, 0.15);
  
  ::v-deep svg {
    color: #ef4444 !important;
    stroke: #ef4444 !important;
  }
  
  &:hover:not(:disabled) {
    background: #fef2f2 !important;
    border-color: #dc2626 !important;
    color: #dc2626 !important;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(239, 68, 68, 0.25);
    
    ::v-deep svg {
      color: #dc2626 !important;
      stroke: #dc2626 !important;
    }
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
}

::v-deep .modal {
  z-index: 1050;
}

::v-deep .modal-backdrop {
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 1040;
}

::v-deep .modal-dialog {
  overflow: visible !important;
}

::v-deep .modal-content {
  border: none;
  border-radius: 20px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.25);
  overflow: visible;
  position: relative;
}

::v-deep .modal-header {
  background: linear-gradient(180deg, #ffffff 0%, #faf9ff 100%);
  border-bottom: 2px solid #e0d8ff;
  padding: 1.5rem 4rem 1.5rem 2rem !important;
  border-radius: 20px 20px 0 0;
  position: relative !important;
  display: flex !important;
  align-items: center !important;
  justify-content: space-between !important;
  overflow: visible !important;
  min-height: 4.5rem !important;
  flex-direction: row !important;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, #7c5dfa 0%, #9d7aff 50%, #cbb9ff 100%);
    z-index: 1;
  }
  
  .modal-title {
    font-size: 1.5rem;
    font-weight: 800;
    color: #2d1b69;
    letter-spacing: -0.3px;
    position: relative;
    z-index: 2;
    flex: 1;
    padding-right: 1.5rem;
    margin-right: 0.5rem;
    order: 1 !important;
    text-align: left !important;
    margin-left: 0 !important;
  }

  // Force close button to be positioned absolutely on the right
  > button:first-child:not(.modal-title),
  > .close:first-child {
    order: 999 !important;
    margin-left: 0 !important;
    margin-right: 0 !important;
  }
}

// Modern Close Button - Positioned on the Right
// Target ALL possible close button selectors with maximum specificity
// Override global modal styles that position close button on the left
// Using multiple selectors to ensure highest specificity
.template-modal .modal-header .close,
.template-modal .modal-header button.close,
.template-modal .modal-header .btn-close,
.template-modal .modal-header button[aria-label="Close"],
.template-modal .modal-header .modal-header-close,
.template-modal .modal-header button[type="button"].close,
.template-modal .modal-header > button:first-child:not(.modal-title),
.template-modal .modal-header > .close:first-child,
::v-deep .template-modal .modal-header .close,
::v-deep .template-modal .modal-header button.close,
::v-deep .template-modal .modal-header .btn-close,
::v-deep .template-modal .modal-header button[aria-label="Close"],
::v-deep .template-modal .modal-header .modal-header-close,
::v-deep .template-modal .modal-header button[type="button"].close,
::v-deep .template-modal .modal-header > button:first-child:not(.modal-title),
::v-deep .template-modal .modal-header > .close:first-child,
::v-deep .template-modal.modal .modal-header .close,
::v-deep .template-modal.modal .modal-header button.close,
::v-deep .template-modal.modal .modal-header .btn-close,
::v-deep .template-modal.modal .modal-header button[aria-label="Close"],
::v-deep .template-modal.modal .modal-header .modal-header-close,
::v-deep .template-modal.modal .modal-header button[type="button"].close,
::v-deep .template-modal.modal .modal-header > button:first-child:not(.modal-title),
::v-deep .template-modal.modal .modal-header > .close:first-child,
::v-deep .modal .modal-header .close,
::v-deep .modal .modal-header button.close,
::v-deep .modal .modal-header .btn-close,
::v-deep .modal .modal-header button[aria-label="Close"],
::v-deep .modal .modal-header .modal-header-close,
::v-deep .modal .modal-header button[type="button"].close,
::v-deep .modal .modal-header > button:first-child:not(.modal-title),
::v-deep .modal .modal-header > .close:first-child,
::v-deep .modal-header .close,
::v-deep .modal-header button.close,
::v-deep .modal-header .btn-close,
::v-deep .modal-header button[aria-label="Close"],
::v-deep .modal-header .modal-header-close,
::v-deep .modal-header button[type="button"].close,
::v-deep .modal-header > button:first-child:not(.modal-title),
::v-deep .modal-header > .close:first-child {
  // Reset ALL positioning from global styles
  margin: 0 !important;
  padding: 0 !important;
  
  // Override global transform that moves button to left
  // Don't reset transform here, we'll set it below for positioning
  
  // Modern button dimensions
  width: 2.75rem !important;
  height: 2.75rem !important;
  min-width: 2.75rem !important;
  min-height: 2.75rem !important;
  max-width: 2.75rem !important;
  max-height: 2.75rem !important;
  
  // Flexbox centering
  display: flex !important;
  align-items: center !important;
  
  // CRITICAL: Absolute positioning on the RIGHT side
  top: 50% !important;
  right: 1.5rem !important;
  transform: translateY(-50%) !important;
  left: auto !important;
  
  // Modern styling
  border-radius: 12px !important;
  background: linear-gradient(135deg, #ffffff 0%, #f8f7ff 100%) !important;
  border: 2px solid #e8e4ff !important;
  box-shadow: 0 2px 8px rgba(124, 93, 250, 0.08), 
              0 1px 3px rgba(0, 0, 0, 0.05) !important;
  
  // Smooth transitions
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
  cursor: pointer !important;
  
  // Remove default button styles
  float: none !important;
  order: 999 !important;
  
  // Icon/X styling
  span,
  &::before,
  &::after {
    font-size: 1.25rem !important;
    line-height: 1 !important;
    color: #7c5dfa !important;
    font-weight: 600 !important;
    display: block !important;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
  }
  
  // Hover state - Enhanced
  &:hover {
    background: linear-gradient(135deg, #7c5dfa 0%, #9d7aff 100%) !important;
    border-color: #7c5dfa !important;
    box-shadow: 0 4px 16px rgba(124, 93, 250, 0.25), 
                0 2px 8px rgba(124, 93, 250, 0.15) !important;
    transform: translateY(-50%) translateY(-2px) scale(1.05) !important;
    right: 1.5rem !important;
    left: auto !important;
    
    span,
    &::before,
    &::after {
      color: #ffffff !important;
      transform: rotate(90deg) !important;
    }
  }
  
  // Active state
  &:active {
    transform: translateY(-50%) scale(0.98) !important;
    box-shadow: 0 2px 8px rgba(124, 93, 250, 0.2) !important;
    right: 1.5rem !important;
    left: auto !important;
  }
  
  // Focus state - Accessibility
  &:focus {
    outline: none !important;
    box-shadow: 0 0 0 4px rgba(124, 93, 250, 0.15),
                0 4px 16px rgba(124, 93, 250, 0.25) !important;
    transform: translateY(-50%) !important;
    right: 1.5rem !important;
    left: auto !important;
  }
  
  // Focus-visible for keyboard navigation
  &:focus-visible {
    outline: 2px solid #7c5dfa !important;
    outline-offset: 2px !important;
  }
}

// Smooth entrance animation for close button when modal opens
::v-deep .template-modal.modal.show .modal-header .close,
::v-deep .template-modal.modal.show .modal-header button.close,
::v-deep .template-modal.modal.show .modal-header .btn-close,
::v-deep .modal.show .template-modal .modal-header .close,
::v-deep .modal.show .template-modal .modal-header button.close,
::v-deep .modal.show .template-modal .modal-header .btn-close {
  animation: closeButtonFadeIn 0.4s cubic-bezier(0.4, 0, 0.2, 1) 0.2s both !important;
}

@keyframes closeButtonFadeIn {
  0% {
    opacity: 0;
    transform: translateX(10px) scale(0.8);
  }
  100% {
    opacity: 1;
    transform: translateX(0) scale(1);
  }
}

// Exclude modal-title from button styling
::v-deep .template-modal .modal-header .modal-title {
  position: relative !important;
  left: auto !important;
  right: auto !important;
  top: auto !important;
  margin: 0 !important;
  padding: 0 !important;
  width: auto !important;
  height: auto !important;
  min-width: auto !important;
  min-height: auto !important;
  float: none !important;
  order: 1 !important;
}

::v-deep .modal-body {
  padding: 2rem;
  background: #ffffff;
}

::v-deep .modal-footer {
  background: #faf9ff;
  border-top: 1px solid #e0d8ff;
  padding: 1.25rem 2rem;
  border-radius: 0 0 20px 20px;
  
  .btn {
    border-radius: 10px;
    font-weight: 600;
    padding: 0.625rem 1.25rem;
    transition: all 0.3s ease;
    
    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    }
  }
}

.status-switch-wrapper {
  background: #f8faff;
  padding: 0.5rem;
  border-radius: 12px;
  border: 1px solid #eef2f7;
  display: inline-flex !important;
}

.status-switch-btn {
  border-radius: 10px !important;
  padding: 0.5rem 1.25rem !important;
  font-weight: 600 !important;
  font-size: 0.875rem !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
  border: none !important;
  
  &.btn-outline-primary {
    background: transparent !important;
    color: #64748b !important;
    
    &:hover {
      background: #ffffff !important;
      color: #7c5dfa !important;
      box-shadow: 0 4px 12px rgba(124, 93, 250, 0.1) !important;
      transform: translateY(-1px);
    }
  }
  
  &.btn-primary {
    background: linear-gradient(135deg, #7c5dfa 0%, #9d7aff 100%) !important;
    color: #ffffff !important;
    box-shadow: 0 4px 12px rgba(124, 93, 250, 0.3) !important;
    
    &:hover {
      transform: translateY(-1px);
      box-shadow: 0 6px 16px rgba(124, 93, 250, 0.4) !important;
    }
  }
}

.modern-input {
  border-radius: 10px;
  border: 1.5px solid #e0d8ff;
  padding: 0.75rem 0.875rem;
  background: #fff;
  transition: all 0.2s ease;
  color: #1e293b;
  font-weight: 500;
  
  &:hover {
    border-color: #b8a9ff;
  }
  
  &:focus {
    border-color: #7c5dfa;
    box-shadow: 0 0 0 3px rgba(124, 93, 250, 0.1);
    outline: none;
  }
  
  &::placeholder {
    color: #94a3b8;
    font-weight: 400;
  }
}

.modern-textarea {
  border-radius: 10px;
  border: 1.5px solid #e0d8ff;
  padding: 0.75rem 0.875rem;
  background: #fff;
  transition: all 0.2s ease;
  color: #1e293b;
  font-weight: 500;
  resize: vertical;
  
  &:hover {
    border-color: #b8a9ff;
  }
  
  &:focus {
    border-color: #7c5dfa;
    box-shadow: 0 0 0 3px rgba(124, 93, 250, 0.1);
    outline: none;
  }
  
  &::placeholder {
    color: #94a3b8;
    font-weight: 400;
  }
}

.quill-editor-wrapper {
  ::v-deep .ql-container {
    height: 200px;
    overflow-y: auto;
    border-radius: 0 0 10px 10px;
    border: 1.5px solid #e0d8ff;
    border-top: none;

    /* Custom Scrollbar */
    &::-webkit-scrollbar {
      width: 5px;
      height: 5px;
    }
    
    &::-webkit-scrollbar-track {
      background: transparent;
    }
    
    &::-webkit-scrollbar-thumb {
      background: #cbb9ff;
      border-radius: 10px;
      transition: background 0.2s ease;
      
      &:hover {
        background: #7c5dfa;
      }
    }
  }
  
  ::v-deep .ql-toolbar {
    border-radius: 10px 10px 0 0;
    border: 1.5px solid #e0d8ff;
    background: #fff;
    
    &:hover {
      border-color: #b8a9ff;
    }
  }
  
  ::v-deep .ql-container.ql-snow:focus-within {
    border-color: #7c5dfa;
    box-shadow: 0 0 0 3px rgba(124, 93, 250, 0.1);
  }
  
  ::v-deep .ql-toolbar.ql-snow:focus-within {
    border-color: #7c5dfa;
  }
}

.btn-modern-cancel {
  border-radius: 10px;
  font-weight: 600;
  padding: 0.625rem 1.5rem;
  border: 1.5px solid #e0d8ff;
  color: #64748b;
  background: #fff;
  transition: all 0.3s ease;
  
  &:hover {
    background: #f8fafc;
    border-color: #b8a9ff;
    color: #475569;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }
}

.btn-modern-primary {
  border-radius: 10px;
  font-weight: 600;
  padding: 0.625rem 1.5rem;
  background: linear-gradient(135deg, #7c5dfa 0%, #9d7aff 100%);
  border: none;
  color: #fff;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  
  &:hover:not(:disabled) {
    background: linear-gradient(135deg, #6d4cdb 0%, #8b6fff 100%);
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(124, 93, 250, 0.4);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
}

/* Redesigned Keywords Panel V2 */
.keywords-panel-v2 {
  background: #f8faff;
  border: 1px solid #eef2f7;
  border-radius: 16px;
  padding: 1.25rem;
  transition: all 0.3s ease;
  
  &:hover {
    border-color: #dbeafe;
    box-shadow: 0 4px 12px rgba(124, 93, 250, 0.03);
  }
}

.panel-icon-wrapper {
  width: 32px;
  height: 32px;
  background: linear-gradient(135deg, #7c5dfa 0%, #9d7aff 100%);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  box-shadow: 0 4px 8px rgba(124, 93, 250, 0.2);
}

.panel-title-v2 {
  font-weight: 700;
  font-size: 0.95rem;
  color: #1e293b;
  letter-spacing: -0.01em;
}

.hover-primary:hover {
  color: #7c5dfa !important;
}

.keywords-scroll-area {
  margin-top: 0.5rem;
  max-height: 160px;
  overflow-y: auto;
  padding-right: 4px;

  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: #e2e8f0;
    border-radius: 10px;
    &:hover { background: #cbd5e1; }
  }
}

.keywords-grid-v2 {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 0.75rem;
}

.modern-keyword-chip-v2 {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 0.5rem 0.75rem;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 4px;
    height: 100%;
    background: transparent;
    transition: all 0.2s ease;
  }

  &:hover {
    transform: translateY(-2px);
    border-color: #7c5dfa;
    box-shadow: 0 4px 12px rgba(124, 93, 250, 0.08);
    background: #fdfcff;

    &::after {
      background: #7c5dfa;
    }

    .chip-action-v2 {
      background: #7c5dfa;
      color: #fff;
      transform: scale(1.1);
    }
    
    .chip-tag-v2 {
      background: #f0ebff;
      color: #7c5dfa;
    }
  }

  &:active {
    transform: translateY(0);
    background: #f5f3ff;
  }
}

.chip-body-v2 {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.chip-label-v2 {
  font-size: 0.825rem;
  font-weight: 600;
  color: #334155;
}

.chip-tag-v2 {
  font-size: 0.7rem;
  font-family: 'Monaco', 'Consolas', monospace;
  color: #64748b;
  background: #f1f5f9;
  padding: 0.15rem 0.4rem;
  border-radius: 4px;
  width: fit-content;
  transition: all 0.2s ease;
}

.chip-action-v2 {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #f1f5f9;
  color: #64748b;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
}
</style>
